import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Upload, Download, Archive, RotateCcw } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { PageHeader, DataTable } from "@/components/common";
import { PatientTable } from "@/components/PatientTable";
import { api, download, errMsg, fmtDate, today, fmtDT } from "@/lib/api";

function ArchivedList() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/patients/archived").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const restore = async (p) => {
    try { await api.post(`/patients/${p.id}/restore`); toast.success(`Patient ${p.patient_id} restored`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <DataTable testid="archived-table" rows={rows} columns={[{ key: "patient_id", label: "Patient ID" }, { key: "medication", label: "Medication" },
      { key: "assigned_employee", label: "Wellness Navigator" }, { key: "archived_at", label: "Archived", render: (r) => fmtDT(r.archived_at) },
      { key: "archived_by_name", label: "Archived By" },
      { key: "_r", label: "", sortable: false, render: (r) => <button className="btn btn-ghost btn-sm" onClick={() => restore(r)} data-testid={`restore-${r.patient_id}`}><RotateCcw size={12} />Restore</button> }]} />
  );
}

export default function Patients({ mine }) {
  const { user, t, can } = useApp();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [archived, setArchived] = useState(false);
  const initial = Object.fromEntries(["medication", "status", "indicator", "assigned_employee_id"].filter((k) => sp.get(k)).map((k) => [k, sp.get(k)]));
  return (
    <div>
      <PageHeader title={t(mine || user.role === "employee" ? "My Patients" : user.role === "supervisor" ? "Team Patients" : "All Patients")}
        subtitle={archived ? "Archived (removed) patients — history preserved" : "Assign Wellness Navigators, track 12-month prescription history and open full records."}
        actions={<>
          {can("action:patient_create") && !archived && <button className="btn btn-primary" onClick={() => nav("/patients/new")} data-testid="add-patient-btn"><UserPlus size={15} />{t("Add New Patient")}</button>}
          {can("action:import") && !archived && <button className="btn btn-ghost" onClick={() => nav("/import")} data-testid="import-patients-btn"><Upload size={15} />{t("Import Patients")}</button>}
          {can("action:export") && !archived && <button className="btn btn-ghost" onClick={() => download("/export/patients?format=xlsx", { filename: `patients_${today()}.xlsx` })} data-testid="export-patients-btn"><Download size={15} />{t("Export")}</button>}
          {user.role === "admin" && <button className="btn btn-ghost" onClick={() => setArchived(!archived)} data-testid="toggle-archived-btn"><Archive size={15} />{archived ? "Back to active list" : "Archived patients"}</button>}
        </>} />
      {archived ? <ArchivedList /> : <PatientTable key={`${mine ? "mine" : "all"}${sp.toString()}`} view={mine ? "mine" : undefined} initialFilters={{ ...initial, include_drafts: "1" }} />}
    </div>
  );
}
